import { useEffect } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { PostForm } from './components/PostForm';
import { useOpportunity, useOpportunityMutations } from './useOpportunities';

/**
 * Edit page for one of my posts. Authenticated route — bounces anonymous
 * visitors to /login. The 15-minute edit window is enforced by the backend;
 * we surface the error verbatim through {@link useApiError} when it fires.
 *
 * <p>If the loaded post isn't authored by the current user (e.g. someone
 * crafted the URL) we redirect to the detail page — the backend would 403
 * anyway, but the redirect avoids letting the form pre-fill with someone
 * else's content.</p>
 */
export function EditOpportunityPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { postId } = useParams<{ postId: string }>();
  const { user, isAuthenticated, isLoading: authLoading, hasRole } = useAuth();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { post, isLoading, isError } = useOpportunity(postId ?? null);
  const { update } = useOpportunityMutations();

  useEffect(() => {
    if (!authLoading && !isAuthenticated && postId) {
      navigate('/login?next=' + encodeURIComponent(`/opportunities/${postId}/edit`), {
        replace: true,
      });
    }
  }, [authLoading, isAuthenticated, postId, navigate]);

  useEffect(() => {
    if (!post || !user) return;
    // Admins (SUPER_ADMIN / ADMIN) can edit any post — the backend
    // mirrors this, bypassing both the ownership check and the 15-min
    // edit window for these roles.
    const isAdmin = hasRole('SUPER_ADMIN') || hasRole('ADMIN');
    if (post.author.userId !== user.id && !isAdmin) {
      navigate(`/opportunities/${post.id}`, { replace: true });
    }
  }, [post, user, navigate, hasRole]);

  if (authLoading || !isAuthenticated) return null;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, md: 5 }, px: { xs: 0, sm: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" sx={{ px: { xs: 1, sm: 0 }, mb: 1 }}>
        <IconButton
          component={RouterLink}
          to={postId ? `/opportunities/${postId}` : '/opportunities'}
          aria-label={t('common.back')}
          edge="start"
        >
          <ArrowBackRoundedIcon />
        </IconButton>
      </Stack>

      {isError ? (
        <Box sx={{ px: { xs: 2, sm: 0 } }}>
          <Alert severity="error">{t('opportunities.postNotFound')}</Alert>
        </Box>
      ) : isLoading || !post ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: { xs: 'none', sm: '1px solid' },
            borderColor: 'divider',
            borderRadius: { xs: 0, sm: 3 },
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 800,
              letterSpacing: -0.3,
              fontSize: { xs: 22, md: 28 },
              mb: 2.5,
            }}
          >
            {t('opportunities.form.editTitle')}
          </Typography>

          <PostForm
            initial={{
              title: post.title,
              body: post.body,
              category: post.category,
              imageThumbUrl: post.imageThumbUrl,
              imageMediumUrl: post.imageMediumUrl,
              imageLargeUrl: post.imageLargeUrl,
            }}
            submitting={update.isPending}
            submitLabel={t('opportunities.form.save')}
            submittingLabel={t('opportunities.form.saving')}
            errorMessage={update.error ? translate(update.error) : null}
            onCancel={() => navigate(`/opportunities/${post.id}`)}
            onSubmit={async (body) => {
              try {
                await update.mutateAsync({ id: post.id, body });
                navigate(`/opportunities/${post.id}`, { replace: true });
              } catch (err) {
                notify.error(translate(err));
              }
            }}
          />
        </Box>
      )}
    </Container>
  );
}
